import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Alert,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  AttachFile,
  Warning as WarningIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  MailOutline as MailOutlineIcon,
  Reply as ReplyIcon,
  ExpandMore as ExpandMoreIcon,
  CardMembership as CardMembershipIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Demarche } from '../types';
import api from '../services/api';

interface AnefDetailsProps {
  demarche: Demarche;
}

const AnefDetails: React.FC<AnefDetailsProps> = ({ demarche }) => {
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  if (!demarche.anef_numero_demande && !demarche.anef_derniere_notification) {
    return null;
  }

  // Parser l'historique des titres pour extraire les dates
  const dernierTitreDetails = demarche.anef_historique_titres && demarche.anef_historique_titres.length > 0
    ? demarche.anef_historique_titres[0]
    : null;

  // Fonction pour télécharger un fichier ANEF
  const handleDownloadFile = async (fileId: string, fileName?: string) => {
    setDownloadingFileId(fileId);
    try {
      const response = await api.get(`/demarches/anef/download/${fileId}?demarcheId=${demarche.id}`, {
        responseType: 'blob',
      });

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `attestation_${fileId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbarMessage('Fichier téléchargé avec succès');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('Erreur téléchargement fichier:', error);
      setSnackbarMessage(error.response?.data?.message || 'Erreur lors du téléchargement du fichier');
      setSnackbarOpen(true);
    } finally {
      setDownloadingFileId(null);
    }
  };

  // Extraire le file ID de l'attestation de dépôt
  // L'attestation peut être dans demande.attestation_depot (dans anef_demandes_complement ou anef_notifications)
  let attestationDepotFileId: string | null = null;
  let attestationDepotFileName: string | null = null;

  console.log('🔍 DEBUG ATTESTATION - Recherche attestation_depot');
  console.log('   anef_demandes_complement:', demarche.anef_demandes_complement?.length || 0);
  console.log('   anef_notifications:', demarche.anef_notifications?.length || 0);

  // Chercher d'abord dans les demandes de complément
  if (demarche.anef_demandes_complement && demarche.anef_demandes_complement.length > 0) {
    const premiere = demarche.anef_demandes_complement[0];
    console.log('   Première demande complément:', premiere);
    console.log('   - demande:', premiere.demande);
    console.log('   - attestation_depot:', premiere.demande?.attestation_depot);

    if (premiere.demande?.attestation_depot) {
      attestationDepotFileId = premiere.demande.attestation_depot.id;
      attestationDepotFileName = premiere.demande.attestation_depot.name;
      console.log('   ✅ Attestation trouvée dans demande complément:', attestationDepotFileId);
    }
  }

  // Si pas trouvé, chercher dans les notifications
  if (!attestationDepotFileId && demarche.anef_notifications && demarche.anef_notifications.length > 0) {
    console.log('   Recherche dans les notifications...');
    for (const notif of demarche.anef_notifications) {
      console.log('   Notification:', notif.motif_notification, '- demande:', notif.demande);
      if (notif.demande?.attestation_depot) {
        attestationDepotFileId = notif.demande.attestation_depot.id;
        attestationDepotFileName = notif.demande.attestation_depot.name;
        console.log('   ✅ Attestation trouvée dans notification:', attestationDepotFileId);
        break;
      }
    }
  }

  // Chercher l'attestation de PROLONGATION dans les notifications de type PROLONGATION_INSTRUCTION
  let attestationProlongationFileId: string | null = null;
  let attestationProlongationFileName: string | null = null;

  if (demarche.anef_notifications && demarche.anef_notifications.length > 0) {
    const notifProlongation = demarche.anef_notifications.find(
      (n: any) => n.motif_notification === 'PROLONGATION_INSTRUCTION'
    );

    console.log('   🔍 Notification PROLONGATION_INSTRUCTION:', notifProlongation);

    if (notifProlongation?.demande?.attestation_depot) {
      attestationProlongationFileId = notifProlongation.demande.attestation_depot.id;
      attestationProlongationFileName = notifProlongation.demande.attestation_depot.name;
      console.log('   ✅ Attestation de prolongation trouvée:', attestationProlongationFileId);
    }
  }

  if (!attestationDepotFileId && !attestationProlongationFileId) {
    console.log('   ⚠️ Aucune attestation trouvée');
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AttachFile /> Informations ANEF
      </Typography>

      {/* Informations générales */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {demarche.anef_numero_demande && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Numéro de demande
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {demarche.anef_numero_demande}
            </Typography>
          </Grid>
        )}

        {demarche.anef_statut_actuel && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Statut ANEF
            </Typography>
            <Chip
              label={demarche.anef_statut_actuel}
              color="info"
              size="small"
            />
          </Grid>
        )}
      </Grid>

      {/* Dernier titre délivré avec détails */}
      {demarche.anef_dernier_titre && (
        <Box sx={{ mb: 3 }}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CardMembershipIcon color="success" />
              Dernier titre délivré
            </Typography>
            {attestationDepotFileId && (
              <Button
                variant="outlined"
                size="small"
                startIcon={downloadingFileId === attestationDepotFileId ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={() => handleDownloadFile(attestationDepotFileId!, attestationDepotFileName || 'attestation_depot.pdf')}
                disabled={downloadingFileId === attestationDepotFileId}
              >
                Télécharger l'attestation
              </Button>
            )}
          </Box>
          <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, borderLeft: 4, borderColor: 'success.main' }}>
            <Typography variant="body1" fontWeight={600} gutterBottom>
              {demarche.anef_dernier_titre}
            </Typography>
            {dernierTitreDetails && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {dernierTitreDetails.date_delivrance && (
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date de délivrance
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {new Date(dernierTitreDetails.date_delivrance).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Grid>
                )}
                {dernierTitreDetails.date_debut_validite && (
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date de début
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {new Date(dernierTitreDetails.date_debut_validite).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Grid>
                )}
                {dernierTitreDetails.date_fin_validite && (
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date de fin
                    </Typography>
                    <Typography variant="body2" fontWeight={500} color="error">
                      {new Date(dernierTitreDetails.date_fin_validite).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Grid>
                )}
                {dernierTitreDetails.motif_delivrance && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Motif de délivrance
                    </Typography>
                    <Typography variant="body2">
                      {dernierTitreDetails.motif_delivrance}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        </Box>
      )}

      {/* Attestation de prolongation */}
      {attestationProlongationFileId && (
        <Box sx={{ mb: 3 }}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CardMembershipIcon color="primary" />
              Attestation de prolongation d'instruction
            </Typography>
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={downloadingFileId === attestationProlongationFileId ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={() => handleDownloadFile(attestationProlongationFileId!, attestationProlongationFileName || 'attestation_prolongation.pdf')}
              disabled={downloadingFileId === attestationProlongationFileId}
            >
              Télécharger l'attestation de prolongation
            </Button>
          </Box>
          <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, borderLeft: 4, borderColor: 'primary.main' }}>
            <Typography variant="body2" color="text.secondary">
              Cette attestation prolonge automatiquement les droits du titulaire pendant l'instruction de sa demande de renouvellement.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Demandes de complément */}
      {demarche.anef_demandes_complement && demarche.anef_demandes_complement.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Demandes de complément ({demarche.anef_demandes_complement.length})
          </Typography>
          <Stack spacing={2}>
            {demarche.anef_demandes_complement.map((demande: any, index: number) => {
              const estLu = demande.lu === true;
              const estRepondu = demande.date_reponse != null;
              const justificatifs = demande.justificatifs || [];

              return (
                <Alert
                  key={index}
                  severity={estRepondu ? "success" : estLu ? "warning" : "error"}
                  icon={estRepondu ? <ReplyIcon /> : estLu ? <MailOutlineIcon /> : <WarningIcon />}
                  sx={{ alignItems: 'flex-start' }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {demande.motif_notification || 'DEMANDE_COMPLEMENT'}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {estRepondu ? (
                          <Chip
                            label="Répondu"
                            size="small"
                            color="success"
                            icon={<CheckCircleOutlineIcon />}
                          />
                        ) : estLu ? (
                          <Chip label="Lu" size="small" color="warning" />
                        ) : (
                          <Chip label="Non lu" size="small" color="error" />
                        )}
                      </Stack>
                    </Box>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Créé le {new Date(demande._created).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>

                    {estLu && demande.date_lecture && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Lu le {new Date(demande.date_lecture).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    )}

                    {estRepondu && demande.date_reponse && (
                      <Typography variant="caption" color="success.main" display="block" fontWeight={500}>
                        Répondu le {new Date(demande.date_reponse).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    )}

                    {/* Afficher les justificatifs demandés */}
                    {justificatifs.length > 0 && (
                      <Accordion sx={{ mt: 2 }} defaultExpanded={!estRepondu}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="caption" fontWeight={600}>
                            📎 Justificatifs demandés ({justificatifs.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={1.5}>
                            {justificatifs.map((justif: any, jIndex: number) => {
                              const echange = justif.justificatif?.historique_echanges?.[justif.index_historique_echanges || 0];
                              return (
                                <Box key={jIndex} sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="caption" fontWeight={600} display="block" color="primary.main">
                                    {justif.justificatif?.type?.replace(/_/g, ' ')}
                                  </Typography>
                                  {echange?.observation && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                                      📝 {echange.observation}
                                    </Typography>
                                  )}
                                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    {echange?.motif && (
                                      <Chip
                                        label={echange.motif.replace(/_/g, ' ')}
                                        size="small"
                                        color={echange.motif === 'DOCUMENT_NON_VALIDE' ? 'error' : 'warning'}
                                      />
                                    )}
                                    {justif.justificatif?.statut && (
                                      <Chip
                                        label={justif.justificatif.statut}
                                        size="small"
                                        color={justif.justificatif.statut === 'VALIDE' ? 'success' : 'default'}
                                      />
                                    )}
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {!estRepondu && (
                      <Alert severity="warning" variant="outlined" sx={{ mt: 1, py: 0.5 }}>
                        <Typography variant="caption">
                          ⚠️ Cette demande nécessite une réponse
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                </Alert>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Toutes les notifications */}
      {demarche.anef_notifications && demarche.anef_notifications.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Divider sx={{ my: 2 }} />
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MailOutlineIcon color="info" />
                Toutes les notifications ({demarche.anef_notifications.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {demarche.anef_notifications.map((notif: any, index: number) => {
                  const estLu = notif.lu === true;
                  const estRepondu = notif.date_reponse != null;

                  return (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        bgcolor: estRepondu ? 'success.50' : estLu ? 'grey.50' : 'warning.50',
                        borderRadius: 1,
                        borderLeft: 3,
                        borderColor: estRepondu ? 'success.main' : estLu ? 'grey.400' : 'warning.main'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {notif.motif_notification}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                          {estRepondu && <Chip label="Répondu" size="small" color="success" />}
                          {!estRepondu && estLu && <Chip label="Lu" size="small" />}
                          {!estLu && <Chip label="Non lu" size="small" color="warning" />}
                        </Stack>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(notif._created).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Historique des titres */}
      {demarche.anef_historique_titres && demarche.anef_historique_titres.length > 1 && (
        <Box>
          <Divider sx={{ my: 2 }} />
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                Historique complet des titres ({demarche.anef_historique_titres.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {demarche.anef_historique_titres.map((titre: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      borderLeft: 3,
                      borderColor: index === 0 ? 'success.main' : 'grey.400'
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {titre.nature} {titre.nature_titre && `(${titre.nature_titre})`}
                    </Typography>
                    {titre.motif_delivrance && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {titre.motif_delivrance}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Du {titre.date_debut_validite && new Date(titre.date_debut_validite).toLocaleDateString('fr-FR')}
                      {titre.date_fin_validite && ` au ${new Date(titre.date_fin_validite).toLocaleDateString('fr-FR')}`}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Paper>
  );
};

export default AnefDetails;
